import React from "react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { userSelector } from "../../features/authSlice";
import { Spin, Select, Row, Col, Tag, Table, Typography } from "antd";
import {
  RiseOutlined,
  DollarOutlined,
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  TeamOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { getDashboard } from "../../api/cartransferService";
import { getUsers } from "../../api/userService";
import { getLegalDocuments } from "../../api/legalDocumentService";
import CartransfersChart from "./CartransfersChart";
import "./Dashboard.css";

const { Title, Text } = Typography;

const { Option } = Select;

const HomePage = () => {
  const { isFetching } = useSelector(userSelector);
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [chartData, setChartData] = useState([]);
  const [prevChartData, setPrevChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientStats, setClientStats] = useState({ persons: 0, companies: 0 });
  const [docCount, setDocCount] = useState(0);

  useEffect(() => {
    const fetchGlobalStats = async () => {
      try {
        const [personsRes, companiesRes, docsRes] = await Promise.all([
          getUsers(1, 1, { entityType: 'person' }),
          getUsers(1, 1, { entityType: 'company' }),
          getLegalDocuments({ limit: 1, page: 1 }),
        ]);
        setClientStats({
          persons: personsRes.data.totalResults || 0,
          companies: companiesRes.data.totalResults || 0,
        });
        setDocCount(docsRes.data.totalResults || 0);
      } catch (e) {
        console.error('Error fetching global stats:', e);
      }
    };
    fetchGlobalStats();
  }, []);

  useEffect(() => {
    let isMounted = true; // 🛡️ bandera de montaje

    const getDashboardData = async () => {
      setLoading(true);
      try {
        const currentRes = await getDashboard(month);
        const prevMonth = (() => {
          const m = parseInt(month, 10);
          return m === 1 ? 12 : m - 1;
        })();
        const prevRes = await getDashboard(prevMonth.toString());
        if (isMounted) {
          setChartData(currentRes.data);
          setPrevChartData(prevRes.data);
        }
      } catch (e) {
        console.error("Error al cargar dashboard:", e);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    getDashboardData();

    return () => {
      isMounted = false; // 🧼 limpieza
    };
  }, [month]);

  const getMonthName = (monthValue) => {
    const months = {
      '1': 'Enero', '2': 'Febrero', '3': 'Marzo', '4': 'Abril',
      '5': 'Mayo', '6': 'Junio', '7': 'Julio', '8': 'Agosto',
      '9': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
    };
    return months[monthValue] || 'Mes';
  };

  if (loading || isFetching) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <Spin size="large" className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* KPIs Cards */}
      <div className="kpis-container">
        <Row gutter={[24, 24]}>
          {(() => {
            const sum = (arr, field) => arr.reduce((a, b) => a + (b[field] || 0), 0);
            const currentTransfers = sum(chartData, 'count');
            const prevTransfers = sum(prevChartData, 'count');
            const currentSales = sum(chartData, 'totalPrice');
            const currentTaxes = sum(chartData, 'totalTax');
            
            // Cálculos para KPIs mejorados
            const delta = (curr, prev) => (prev ? ((curr - prev) / prev) * 100 : (curr > 0 ? 100 : 0));
            const avgValuePerTransfer = currentTransfers > 0 ? currentSales / currentTransfers : 0;
            const avgTaxPerTransfer = currentTransfers > 0 ? currentTaxes / currentTransfers : 0;
            const growthRate = delta(currentTransfers, prevTransfers);
            
            // Calcular tendencia semanal (últimos 7 días vs 7 días anteriores)
            const last7Days = chartData.slice(-7);
            const prev7Days = prevChartData.slice(-7);
            const currentWeekTransfers = sum(last7Days, 'count');
            const prevWeekTransfers = sum(prev7Days, 'count');
            const weeklyTrend = delta(currentWeekTransfers, prevWeekTransfers);

            return (
              <>
                {/* KPI 1: Transferencias del Mes */}
                <Col xs={24} sm={12} lg={6}>
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <RiseOutlined className="kpi-icon" style={{ color: '#667eea' }} />
                      <div className="kpi-title">Transferencias del Mes</div>
                    </div>
                    <div className="kpi-value">{currentTransfers}</div>
                    <div className="kpi-change">
                      <Tag className={`kpi-change-tag ${growthRate >= 0 ? 'positive' : 'negative'}`}>
                        {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
                      </Tag>
                    </div>
                    <div className="kpi-description">
                      vs mes anterior: {prevTransfers}
                    </div>
                    <div className="kpi-metric">
                      📈 Tasa de crecimiento mensual
                    </div>
                  </div>
                </Col>

                {/* KPI 2: Valor Promedio */}
                <Col xs={24} sm={12} lg={6}>
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <DollarOutlined className="kpi-icon" style={{ color: '#20c997' }} />
                      <div className="kpi-title">Valor Promedio</div>
                    </div>
                    <div className="kpi-value">${Math.round(avgValuePerTransfer).toLocaleString('es-CL')}</div>
                    <div className="kpi-description">
                      por transferencia
                    </div>
                    <div className="kpi-metric">
                      💰 Ticket promedio
                    </div>
                  </div>
                </Col>

                {/* KPI 3: Impuesto Promedio */}
                <Col xs={24} sm={12} lg={6}>
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <BankOutlined className="kpi-icon" style={{ color: '#fa8c16' }} />
                      <div className="kpi-title">Impuesto Promedio</div>
                    </div>
                    <div className="kpi-value">${Math.round(avgTaxPerTransfer).toLocaleString('es-CL')}</div>
                    <div className="kpi-description">
                      por transferencia
                    </div>
                    <div className="kpi-metric">
                      🏛️ Tributación promedio
                    </div>
                  </div>
                </Col>

                {/* KPI 4: Tendencia Semanal */}
                <Col xs={24} sm={12} lg={6}>
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <BarChartOutlined className="kpi-icon" style={{ color: '#722ed1' }} />
                      <div className="kpi-title">Tendencia Semanal</div>
                    </div>
                    <div className="kpi-value">{currentWeekTransfers}</div>
                    <div className="kpi-change">
                      <Tag className={`kpi-change-tag ${weeklyTrend >= 0 ? 'positive' : 'negative'}`}>
                        {weeklyTrend >= 0 ? '+' : ''}{weeklyTrend.toFixed(1)}%
                      </Tag>
                    </div>
                    <div className="kpi-description">
                      últimos 7 días
                    </div>
                    <div className="kpi-metric">
                      📊 vs semana anterior
                    </div>
                  </div>
                </Col>
              </>
            );
          })()}
        </Row>

        {/* Row 2: Clientes y Documentos */}
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12}>
            <div className="kpi-card">
              <div className="kpi-header">
                <TeamOutlined className="kpi-icon" style={{ color: '#13c2c2' }} />
                <div className="kpi-title">Clientes Registrados</div>
              </div>
              <div className="kpi-value">{(clientStats.persons + clientStats.companies).toLocaleString('es-CL')}</div>
              <div className="kpi-description">total en el sistema</div>
              <div className="kpi-metric">
                👤 {clientStats.persons.toLocaleString('es-CL')} personas · 🏢 {clientStats.companies.toLocaleString('es-CL')} empresas
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div className="kpi-card">
              <div className="kpi-header">
                <FileTextOutlined className="kpi-icon" style={{ color: '#eb2f96' }} />
                <div className="kpi-title">Documentos Generados</div>
              </div>
              <div className="kpi-value">{docCount.toLocaleString('es-CL')}</div>
              <div className="kpi-description">hasta la fecha</div>
              <div className="kpi-metric">
                📄 Total de documentos notariales
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Charts Container */}
      <div className="charts-container">
        <div className="charts-header">
          <div>
            <Title level={3} className="charts-title">
              Análisis de Transferencias - {getMonthName(month)}
            </Title>
            <Text className="charts-subtitle">
              Evolución temporal de transferencias
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, color: '#666', margin: 0 }}>
              <CalendarOutlined style={{ marginRight: 6 }} />
              Mes:
            </label>
            <Select
              value={month}
              onChange={setMonth}
              style={{ width: 140 }}
              placeholder="Seleccionar mes"
            >
              <Option value="1">Enero</Option>
              <Option value="2">Febrero</Option>
              <Option value="3">Marzo</Option>
              <Option value="4">Abril</Option>
              <Option value="5">Mayo</Option>
              <Option value="6">Junio</Option>
              <Option value="7">Julio</Option>
              <Option value="8">Agosto</Option>
              <Option value="9">Septiembre</Option>
              <Option value="10">Octubre</Option>
              <Option value="11">Noviembre</Option>
              <Option value="12">Diciembre</Option>
            </Select>
          </div>
        </div>
        
        <CartransfersChart data={chartData} />
      </div>

      {/* Resumen Semanal */}
      {(() => {
        const byWeek = {};
        chartData.forEach((d) => {
          const day = d._id?.day || 1;
          const week = Math.ceil(day / 7);
          if (!byWeek[week]) byWeek[week] = { count: 0, totalPrice: 0, totalTax: 0 };
          byWeek[week].count += d.count || 0;
          byWeek[week].totalPrice += d.totalPrice || 0;
          byWeek[week].totalTax += d.totalTax || 0;
        });
        const weeks = Object.keys(byWeek)
          .map((w) => ({ week: parseInt(w, 10), ...byWeek[w] }))
          .sort((a, b) => a.week - b.week);

        const columns = [
          { title: 'Semana', dataIndex: 'week', key: 'week', width: 80 },
          { title: 'Transf.', dataIndex: 'count', key: 'count', width: 90 },
          {
            title: 'Ventas', dataIndex: 'totalPrice', key: 'totalPrice',
            render: (v) => `$${(v || 0).toLocaleString()}`,
          },
          {
            title: 'Impuestos', dataIndex: 'totalTax', key: 'totalTax',
            render: (v) => `$${(v || 0).toLocaleString()}`,
          },
        ];

        return (
          <div className="charts-container">
            <div className="charts-header">
              <div>
                <Title level={4} className="charts-title">
                  Resumen Semanal
                </Title>
                <Text className="charts-subtitle">
                  Desglose por semanas del mes de {getMonthName(month)}
                </Text>
              </div>
            </div>
            <Table
              size="small"
              columns={columns}
              dataSource={weeks}
              rowKey={(r) => r.week}
              pagination={{ pageSize: 4, hideOnSinglePage: true, defaultCurrent: Math.max(1, Math.ceil(weeks.length / 4)) }}
            />
          </div>
        );
      })()}
    </div>
  );
};

export default HomePage;
